import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { PrepareSignature } from "./index";
import type { FieldData } from "./index";
import type { SignerOption } from "./utils/SignatureSidebar";

export type PlacementMode = "appended_page" | "inline_fields";

export const ModeCard = ({
  selected,
  disabled,
  iconName,
  title,
  description,
  badge,
  onClick,
}: {
  selected: boolean;
  disabled?: boolean;
  iconName: string;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-pressed={selected}
    className={`relative flex flex-col items-start gap-2 p-3 rounded-xl border-2 text-left transition-all duration-150 ${
      disabled
        ? "opacity-50 cursor-not-allowed border-neutral-200 bg-neutral-50"
        : selected
          ? "border-neutral-900 bg-neutral-50 shadow-sm"
          : "border-neutral-200 hover:border-neutral-400 hover:shadow-sm bg-white"
    }`}
  >
    {badge && (
      <span className="absolute top-2 right-2 text-h5 font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        {badge}
      </span>
    )}
    <div className="flex flex-row items-center gap-2">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          selected ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-500"
        }`}
      >
        <Icon1 name={iconName} size={16} color={selected ? "white" : undefined} />
      </div>
      <div className="text-h3 font-semibold text-neutral-900">{title}</div>
    </div>
    <div className="text-h4 text-neutral-500 leading-snug">{description}</div>
  </button>
);

export const PlacementModeSelector = ({
  mode,
  onChange,
  inlineDisabled,
  inlineDisabledReason,
}: {
  mode: PlacementMode;
  onChange: (mode: PlacementMode) => void;
  inlineDisabled: boolean;
  inlineDisabledReason?: string;
}) => (
  <div className="flex flex-col gap-2">
    <div className="text-h3 font-semibold text-neutral-700">Mode de signature</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <ModeCard
        selected={mode === "appended_page"}
        iconName="file-text"
        title="Page récapitulative"
        description="Ajoute une page de signatures à la fin du document."
        onClick={() => onChange("appended_page")}
      />
      <ModeCard
        selected={mode === "inline_fields"}
        disabled={inlineDisabled}
        iconName="sign"
        title="Champs dans le document"
        description="Placez les zones de signature directement sur le document."
        badge="Nouveau"
        onClick={() => onChange("inline_fields")}
      />
    </div>
    {inlineDisabled && inlineDisabledReason && (
      <div className="text-h5 text-neutral-400">{inlineDisabledReason}</div>
    )}
  </div>
);

export const InlineFieldsSummary = ({
  count,
  disabled,
  onEdit,
  onClear,
  perSignerCounts,
}: {
  count: number;
  disabled?: boolean;
  onEdit: () => void;
  onClear: () => void;
  /** Optional: per-signer coverage so the user sees who still needs a field. */
  perSignerCounts?: { id: string; name: string; count: number }[];
}) => {
  const empty = count === 0;
  const missingSigners =
    perSignerCounts?.filter((s) => s.count === 0).map((s) => s.name) ?? [];
  return (
    <div
      className={`flex flex-col gap-2 p-3 rounded-xl border ${
        empty
          ? "border-dashed border-neutral-300 bg-neutral-50"
          : "border-emerald-200 bg-emerald-50"
      }`}
    >
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2 min-w-0">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
              empty ? "bg-neutral-200 text-neutral-500" : "bg-emerald-500 text-white"
            }`}
          >
            <Icon1
              name={empty ? "plus" : "sign"}
              size={16}
              color={empty ? undefined : "white"}
            />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="text-h3 font-medium text-neutral-900">
              {empty
                ? "Aucun champ placé"
                : `${count} champ${count > 1 ? "s" : ""} placé${count > 1 ? "s" : ""}`}
            </div>
            <div className="text-h4 text-neutral-500">
              {empty
                ? "Placez les zones de signature sur le document."
                : "Les champs seront utilisés pour la signature."}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-2 shrink-0">
          {!empty && (
            <SecondaryButton variant="small" onClick={onClear}>
              Réinitialiser
            </SecondaryButton>
          )}
          <Button
            variant="small"
            label={empty ? "Placer les champs" : "Modifier"}
            onClick={onEdit}
            disabled={disabled}
          />
        </div>
      </div>
      {missingSigners.length > 0 && (
        <div className="text-h5 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
          Signataire(s) sans champ : {missingSigners.join(", ")}
        </div>
      )}
    </div>
  );
};

/**
 * Full-screen overlay wrapping PrepareSignature, with a header and close
 * button. Both SignatureSetupModal and DropFileModal use the same chrome
 * so the inline-fields experience is identical.
 */
export const PlaceFieldsOverlay = ({
  open,
  pdfUrl,
  documentName,
  initialFields,
  initialDimensions,
  signers,
  onClose,
  onConfirm,
  onSignerInitialsChange,
}: {
  open: boolean;
  pdfUrl: string | null;
  documentName: string;
  initialFields: FieldData[];
  initialDimensions: { width: number; height: number } | null;
  signers?: SignerOption[];
  onClose: () => void;
  onConfirm: (fields: FieldData[], dimensions: { width: number; height: number }) => void;
  /** Forward initials edits from the sidebar back up to the caller. */
  onSignerInitialsChange?: (signerId: string, initials: string) => void;
}) => {
  if (!open || !pdfUrl) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1400px] h-[92vh] overflow-hidden flex flex-col">
        <div className="flex flex-row items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white">
          <div className="flex flex-row items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center">
              <Icon1 name="sign" size={16} color="white" />
            </div>
            <div className="flex flex-col">
              <div className="text-h3 font-semibold text-neutral-900">
                Placer les champs de signature
              </div>
              <div className="text-h5 text-neutral-500">{documentName}</div>
            </div>
          </div>
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
        </div>
        <div className="flex-1 overflow-hidden">
          <PrepareSignature
            pdfUrl={pdfUrl}
            readyButtonLabel="Valider les champs"
            initialFields={initialFields}
            initialDimensions={initialDimensions ?? undefined}
            signers={signers}
            onClose={onClose}
            onReadyForSignature={async (fields, dimensions) => {
              onConfirm(fields, dimensions);
            }}
            onSignerInitialsChange={onSignerInitialsChange}
          />
        </div>
      </div>
    </div>
  );
};
